import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { mlApi, Anomaly } from '../../services/api/ml.api';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';

export default function AnomaliesPage() {
  const { t } = useTranslation(['ml', 'common']);
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [selectedAnomaly, setSelectedAnomaly] = useState<Anomaly | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [rootCauseAnalysis, setRootCauseAnalysis] = useState<any>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 24 * 60 * 60 * 1000,
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '24h',
  });

  useEffect(() => {
    fetchData();
  }, [selectedAppId]);

  useEffect(() => {
    if (selectedAnomaly && showDetailPanel) {
      fetchRootCauseAnalysis(selectedAnomaly.id);
    }
  }, [selectedAnomaly, showDetailPanel]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mlApi.getAnomalies(100, selectedAppId);
      setAnomalies(data);
      if (data.length > 0) {
        setSelectedAnomaly(data[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load anomalies');
      console.error('Anomalies fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRootCauseAnalysis = async (anomalyId: string) => {
    try {
      setLoadingAnalysis(true);
      const analysis = await mlApi.analyzeAnomalyRootCauses(anomalyId);
      setRootCauseAnalysis(analysis);
    } catch (err: any) {
      console.error('Failed to analyze root causes:', err);
      setRootCauseAnalysis(null);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const updateAnomalyStatus = async (status: 'new' | 'investigating' | 'resolved' | 'false_positive') => {
    if (!selectedAnomaly) return;

    try {
      const updatedAnomaly = await mlApi.updateAnomalyStatus(selectedAnomaly.id, status);

      // Update the anomaly in the list
      setAnomalies(anomalies.map(a => a.id === updatedAnomaly.id ? updatedAnomaly : a));

      // Update selected anomaly
      setSelectedAnomaly(updatedAnomaly);
    } catch (err: any) {
      console.error('Failed to update anomaly status:', err);
      setError(err.message || 'Failed to update status');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'investigating':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'false_positive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  // Remove unused hardcoded functions - now using backend AI analysis

  // Generate AI-powered insights for anomalies
  const getAnomalyInsights = (anomaly: Anomaly) => {
    const insights = [];
    const deviationPercent = anomaly.deviation_percent || 0;
    const severity = anomaly.severity;

    // Business Impact
    if (severity === 'critical') {
      insights.push({
        type: 'business_impact',
        icon: '💼',
        title: t('ml:anomalies.insights.businessImpact.critical.title'),
        description: t('ml:anomalies.insights.businessImpact.critical.description', {
          metricName: anomaly.metric_name || 'system performance'
        }),
        priority: 'critical',
      });
    } else if (severity === 'high') {
      insights.push({
        type: 'business_impact',
        icon: '⚠️',
        title: t('ml:anomalies.insights.businessImpact.high.title'),
        description: t('ml:anomalies.insights.businessImpact.high.description', {
          metricName: anomaly.metric_name || 'service quality'
        }),
        priority: 'high',
      });
    }

    // Deviation Analysis
    if (Math.abs(deviationPercent) > 100) {
      insights.push({
        type: 'trend',
        icon: '📈',
        title: t('ml:anomalies.insights.deviation.extreme.title'),
        description: t('ml:anomalies.insights.deviation.extreme.description', {
          percent: Math.abs(deviationPercent).toFixed(1)
        }),
        priority: 'high',
      });
    } else if (Math.abs(deviationPercent) > 50) {
      insights.push({
        type: 'trend',
        icon: '📊',
        title: t('ml:anomalies.insights.deviation.significant.title'),
        description: t('ml:anomalies.insights.deviation.significant.description', {
          percent: Math.abs(deviationPercent).toFixed(1)
        }),
        priority: 'medium',
      });
    }

    // Pattern Recognition
    const timeAgo = Date.now() - new Date(anomaly.detected_at).getTime();
    const hoursAgo = Math.floor(timeAgo / (1000 * 60 * 60));

    if (hoursAgo < 1) {
      insights.push({
        type: 'timing',
        icon: '⏰',
        title: t('ml:anomalies.insights.timing.recent.title'),
        description: t('ml:anomalies.insights.timing.recent.description', {
          minutes: Math.floor(timeAgo / (1000 * 60))
        }),
        priority: 'info',
      });
    }

    return insights;
  };

  const filteredAnomalies =
    filter === 'all'
      ? anomalies
      : anomalies.filter((a) => a.severity === filter || a.status === filter);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t('ml:anomalies.loading')}</div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('ml:anomalies.title')}
          <span className="ml-3 inline-flex items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-3 py-1 text-sm font-semibold text-white">
            {t('ml:aiPowered')}
          </span>
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('ml:anomalies.subtitle')}</p>
      </div>

      {/* Stats */}
      <div className="mb-6 sm:mb-8 grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('common:labels.total')}</p>
          <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">{anomalies.length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('ml:anomalies.critical')}</p>
          <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-red-600 dark:text-red-400">
            {anomalies.filter((a) => a.severity === 'critical').length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('ml:anomalies.new')}</p>
          <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-yellow-600 dark:text-yellow-400">
            {anomalies.filter((a) => a.status === 'new').length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('ml:anomalies.avgScore')}</p>
          <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">
            {anomalies.length > 0
              ? (
                  anomalies.reduce((sum, a) => sum + (a.anomaly_score || 0), 0) / anomalies.length
                ).toFixed(1)
              : '0.0'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-2">
        {['all', 'critical', 'high', 'medium', 'new', 'resolved'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Anomalies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAnomalies.length === 0 ? (
          <div className="col-span-full rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <p className="text-gray-600 dark:text-gray-400">{t('ml:anomalies.noAnomalies')}</p>
          </div>
        ) : (
          filteredAnomalies.map((anomaly) => (
            <div
              key={anomaly.id}
              onClick={() => {
                setSelectedAnomaly(anomaly);
                setShowDetailPanel(true);
              }}
              className={`cursor-pointer rounded-xl border p-6 shadow-sm transition-all hover:shadow-lg hover:-translate-y-1 ${
                selectedAnomaly?.id === anomaly.id
                  ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 dark:border-indigo-500'
                  : 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 hover:border-indigo-300 dark:hover:border-indigo-700'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span
                    className={`inline-flex h-3 w-3 rounded-full ${
                      anomaly.severity === 'critical'
                        ? 'bg-red-500 animate-pulse shadow-lg shadow-red-500/50'
                        : anomaly.severity === 'high'
                        ? 'bg-orange-500 shadow-lg shadow-orange-500/50'
                        : anomaly.severity === 'medium'
                        ? 'bg-yellow-500'
                        : 'bg-blue-500'
                    }`}
                  ></span>
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${getSeverityColor(anomaly.severity)}`}>
                    {anomaly.severity.toUpperCase()}
                  </span>
                </div>
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusColor(anomaly.status)}`}>
                  {anomaly.status}
                </span>
              </div>

              {/* Content */}
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {anomaly.metric_name || `${anomaly.anomaly_type} Anomaly`}
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{(() => {
                    const minutes = Math.floor((Date.now() - new Date(anomaly.detected_at).getTime()) / (1000 * 60));
                    if (minutes < 60) return `${minutes}m ago`;
                    const hours = Math.floor(minutes / 60);
                    if (hours < 24) return `${hours}h ago`;
                    const days = Math.floor(hours / 24);
                    return `${days}d ago`;
                  })()}</span>
                </div>
              </div>

              {/* Metrics */}
              <div className="space-y-3">
                {anomaly.anomaly_score !== undefined && anomaly.anomaly_score !== null && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('ml:anomalies.anomalyScore')}</span>
                    <div className="flex items-center space-x-2">
                      <div className="h-2 w-24 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-yellow-500 to-red-500 rounded-full"
                          style={{ width: `${anomaly.anomaly_score * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
                        {(anomaly.anomaly_score * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                )}

                {anomaly.deviation_percent !== undefined && anomaly.deviation_percent !== null && (
                  <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('ml:anomalies.deviation')}</span>
                    <span className="text-lg font-bold text-red-600 dark:text-red-400">
                      {anomaly.deviation_percent > 0 ? '+' : ''}{anomaly.deviation_percent.toFixed(1)}%
                    </span>
                  </div>
                )}

                {anomaly.actual_value !== undefined && anomaly.actual_value !== null &&
                 anomaly.expected_value !== undefined && anomaly.expected_value !== null && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('ml:anomalies.observed')}</p>
                      <p className="text-sm font-bold text-red-600 dark:text-red-400">{anomaly.actual_value.toFixed(1)}</p>
                    </div>
                    <div className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('ml:anomalies.expected')}</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{anomaly.expected_value.toFixed(1)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center space-x-1">
                  <span>{t('ml:anomalies.viewDetails')}</span>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detailed Analysis Panel */}
      {selectedAnomaly && showDetailPanel && (
        <div className="mt-8 space-y-6">
            {/* Header */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex h-4 w-4 rounded-full ${selectedAnomaly.severity === 'critical' ? 'bg-red-500 animate-pulse' : selectedAnomaly.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'}`}></span>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedAnomaly.metric_name || `${selectedAnomaly.anomaly_type} Anomaly`}
                    </h2>
                  </div>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Detected {new Date(selectedAnomaly.detected_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailPanel(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Status Update Actions */}
              <div className="mt-6 border-t border-gray-200 dark:border-gray-800 pt-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</p>
                    <p className={`mt-1 inline-flex rounded-full px-3 py-1 text-sm font-semibold ${getStatusColor(selectedAnomaly.status)}`}>
                      {selectedAnomaly.status}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedAnomaly.status !== 'investigating' && (
                      <button
                        onClick={() => updateAnomalyStatus('investigating')}
                        className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-yellow-700 bg-yellow-100 rounded-lg hover:bg-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:hover:bg-yellow-900/30 transition-colors"
                      >
                        <span className="hidden sm:inline">Mark as </span>Investigating
                      </button>
                    )}
                    {selectedAnomaly.status !== 'resolved' && (
                      <button
                        onClick={() => updateAnomalyStatus('resolved')}
                        className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30 transition-colors"
                      >
                        <span className="hidden sm:inline">Mark as </span>Resolved
                      </button>
                    )}
                    {selectedAnomaly.status !== 'false_positive' && (
                      <button
                        onClick={() => updateAnomalyStatus('false_positive')}
                        className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                      >
                        <span className="hidden sm:inline">Mark as </span>False Positive
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {selectedAnomaly.actual_value !== undefined && selectedAnomaly.actual_value !== null && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('ml:anomalies.observedValue')}</p>
                    <p className="mt-1 text-2xl font-bold text-red-600 dark:text-red-400">
                      {selectedAnomaly.actual_value.toFixed(2)}
                    </p>
                  </div>
                )}
                {selectedAnomaly.expected_value !== undefined && selectedAnomaly.expected_value !== null && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('ml:anomalies.expectedValue')}</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedAnomaly.expected_value.toFixed(2)}
                    </p>
                  </div>
                )}
                {selectedAnomaly.deviation_percent !== undefined && selectedAnomaly.deviation_percent !== null && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('ml:anomalies.deviation')}</p>
                    <p className="mt-1 text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {selectedAnomaly.deviation_percent > 0 ? '+' : ''}{selectedAnomaly.deviation_percent.toFixed(1)}%
                    </p>
                  </div>
                )}
                {selectedAnomaly.anomaly_score !== undefined && selectedAnomaly.anomaly_score !== null && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('ml:anomalies.anomalyScore')}</p>
                    <p className="mt-1 text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      {selectedAnomaly.anomaly_score.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* AI Insights */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-2xl">🤖</span>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('ml:anomalies.detailPanel.aiInsights')}</h3>
                <span className="inline-flex items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-2.5 py-0.5 text-xs font-semibold text-white">
                  {t('ml:aiPowered')}
                </span>
              </div>
              <div className="space-y-3">
                {getAnomalyInsights(selectedAnomaly).map((insight, idx) => (
                  <div key={idx} className={`p-4 rounded-lg ${insight.priority === 'critical' ? 'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800' : insight.priority === 'high' ? 'bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800' : 'bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800'}`}>
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl">{insight.icon}</span>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{insight.title}</h4>
                        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{insight.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI-Powered Root Cause Analysis from Backend */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-2xl">🔍</span>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('ml:anomalies.detailPanel.rootCauses')}</h3>
              </div>
              <div className="space-y-4">
                {loadingAnalysis ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Analyzing root causes...</p>
                  </div>
                ) : (rootCauseAnalysis?.causes || []).map((cause, idx) => (
                  <div key={idx} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="font-semibold text-gray-900 dark:text-white">{cause.category}</h4>
                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-gray-500">Confidence:</span>
                            <div className="flex items-center">
                              <div className="h-2 w-24 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${cause.confidence >= 70 ? 'bg-green-500' : cause.confidence >= 50 ? 'bg-yellow-500' : 'bg-gray-400'}`}
                                  style={{ width: `${cause.confidence}%` }}
                                ></div>
                              </div>
                              <span className="ml-2 text-xs font-medium text-gray-700 dark:text-gray-300">{cause.confidence}%</span>
                            </div>
                          </div>
                        </div>
                        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{cause.description}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {cause.indicators.map((indicator, i) => (
                            <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400">
                              {indicator}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
    </MainLayout>
  );
}
